import {
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  H1,
  H3,
  Textarea,
  Toggles,
  UserCombobox,
} from '@/components'
import VehicleSelect from '@/components/vehicle-select'
import { MonthPicker } from '@/components_deprecated'
import { Field } from '@/modules/companies'
import { getVehicleExpirationTypeName } from '@/modules/vehicles/util'
import { durationAsSecondsToString, format } from '@/utils'
import { isInvalidDate } from '@/utils/time'
import { useMutation, useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  addMinutes,
  differenceInMinutes,
  endOfMonth,
  formatISO,
  isSameDay,
  parseISO,
  startOfMonth,
  subMinutes,
} from 'date-fns'
import { MapIcon } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { Map as ActivityMap } from '../components/detail'
import { UPDATE_DRIVING_WITHOUT_CARD_EVENT_EXPLANATION } from '../mutations'
import {
  DRIVER_LOCATIONS_FOR_VEHICLE_PERIOD,
  VEHICLE_ACTIVITIES_WITHOUT_CARD,
} from '../queries'
import type {
  DriverLocationForMap,
  DrivingWithoutCardEventNode,
  DrivingWithoutCardNearbyVehicleExpirationNode,
  DrivingWithoutCardNeighbourActivityNode,
} from '../types.graphql'
import { generateActivityMapColors } from '../util'

const RELATED_EVENT_GAP_MINUTES = 6 * 60
const DRIVER_LOCATION_PERIOD_PADDING_MINUTES = 30
const PADDED_DRIVER_LOCATION_COLOR = '#8A8A9A'
const EVENT_DRIVER_LOCATION_SEGMENT = 'event'
const BEFORE_DRIVER_LOCATION_SEGMENT = 'before'
const AFTER_DRIVER_LOCATION_SEGMENT = 'after'

type ExplanationStatus = 'EXPLAINED' | 'UNEXPLAINED'

type ActivityWithoutCardGroup = {
  id: string
  vehicleLabel: string
  startDate: Date
  endDate: Date
  durationMinutes: number
  previousDriverName: string | null
  nextDriverName: string | null
  entries: DrivingWithoutCardEventNode[]
}

export const DrivingWithoutCard: React.FC = () => {
  const translations = useTranslate({
    salaryCode: 'common.salary-code',
    amount: 'common.amount',
    activities: 'common.activities',
    workDay: 'common.work-day',
    dayRest: 'common.day-rest',
    weekRest: 'common.week-rest',
    showRest: 'common.show-rest',
    export: 'common.export',
    period: 'common.period',
    weekN: ['common.week-n', { n: 0 }],
    workOverview: 'salary.work-overview',
    manualRegistrations: 'activities.manual-registrations',
    minutes: 'common.minutes',
    duration: 'common.duration',
    events: 'common.events',
    unknown: 'common.unknown',
    map: 'common.map',
    loading: 'common.loading',
    noData: 'common.no-data',
    drivingWithoutCard: 'activities.driving-without-card',
    driverBefore: 'activities.driver-before',
    driverAfter: 'activities.driver-after',
    vehicleExpirations: 'vehicles.vehicle-expirations',

    nUsersNeedApproval: ['salary.n-users-need-approval', { n: 0 }],
    oneUserNeedsApproval: 'salary.one-user-needs-approval',
    nWorkPeriods: ['salary.n-work-periods', { n: 0 }],

    sumForMonth: ['activities.sum-for-month-title', { month: '' }],
    driving: 'activities.driving',
    otherWork: 'common.other-work',
    overtime: 'common.overtime',
    available: 'activities.availability-time',
    lockedValue: 'salary.locked-value',
    lock: 'salary.lock',

    toast: {
      mailSuccess: 'exports.success-email-process',
    },
    error: {
      generic: 'error.generic-server-error',
    },

    salaryMode: 'common.salary-mode',
    month: 'common.month',
    user: 'common.user',
    vehicle: 'common.vehicle',
    chooseGroup: 'common.choose-group',
    sameDriverBeforeAndAfter: 'activities.same-driver-before-and-after',
    comment: 'common.comment',
    date: 'common.date',
    description: 'common.description',
    save: 'common.save',
    status: 'common.status',
    explained: 'activities.explained',
    notExplained: 'activities.not-explained',

    overtimeThatOverlapsMonths: 'salary.overtime-that-overlaps-months',
    nextMonth: 'common.next-month',
    workPeriods: 'salary.work-periods',
    confirmHours: 'salary.confirm-hours',

    showResolved: 'salary.show-resolved',
    showOk: 'activities.show-ok',

    leaderGroups: 'companies.leader-groups',
    noManualRegistration: 'salary.no-manual-registration',
  })

  const form = useForm<{
    vehicle: string
    previousDriverUser: string
    nextDriverUser: string
    sameDriverBeforeAndAfter: boolean
    explanationStatus: ExplanationStatus
    datetimeStart: Date
    datetimeEnd: Date
  }>({
    values: {
      vehicle: '',
      previousDriverUser: '',
      nextDriverUser: '',
      sameDriverBeforeAndAfter: false,
      explanationStatus: 'UNEXPLAINED',
      datetimeStart: startOfMonth(new Date()),
      datetimeEnd: endOfMonth(new Date()),
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'driving-without-card-filters',
        transformValues: {
          vehicle: val => (typeof val === 'string' ? val : ''),
          previousDriverUser: val => (typeof val === 'string' ? val : ''),
          nextDriverUser: val => (typeof val === 'string' ? val : ''),
          sameDriverBeforeAndAfter: val => val === true,
          explanationStatus: val =>
            val === 'EXPLAINED' || val === 'UNEXPLAINED' ? val : 'UNEXPLAINED',
          datetimeStart: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
          datetimeEnd: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
        },
      },
    },
  })

  const { data } = useQuery(VEHICLE_ACTIVITIES_WITHOUT_CARD, {
    variables: {
      datetimeStart: formatISO(form.formValues.datetimeStart),
      datetimeEnd: formatISO(form.formValues.datetimeEnd),
      vehicleId: form.formValues.vehicle || null,
      previousDriverUserId: form.formValues.previousDriverUser || null,
      nextDriverUserId: form.formValues.nextDriverUser || null,
      sameDriverBeforeAndAfter: form.formValues.sameDriverBeforeAndAfter
        ? true
        : null,
      explained: form.formValues.explanationStatus === 'EXPLAINED',
    },
  })

  const activityGroups = useMemo(
    () =>
      groupRelatedActivitiesWithoutCard(
        (data?.vehicleActivitiesWithoutCard ??
          []) as DrivingWithoutCardEventNode[],
        translations.unknown
      ),
    [data?.vehicleActivitiesWithoutCard, translations.unknown]
  )

  const explanationStatusItems: {
    value: ExplanationStatus
    label: string
  }[] = [
    { value: 'UNEXPLAINED', label: translations.notExplained },
    { value: 'EXPLAINED', label: translations.explained },
  ]

  return (
    <div className="relative box-border px-4 py-4 md:px-[52px] md:py-8 md:pb-[52px]">
      <div className="flex gap-2">
        <H1>{translations.drivingWithoutCard} </H1>
      </div>
      <Card className="mb-4 p-0" contentClassName="grid grid-cols-1">
        <section className="grid grid-cols-1 gap-4 p-4 md:grid-cols-[repeat(6,minmax(0,1fr))]">
          <Field>
            <label>{translations.month}</label>
            <MonthPicker
              value={form.formValues.datetimeStart}
              onChange={date => {
                const start = startOfMonth(date)
                const end = endOfMonth(date)
                form.updateForm({
                  datetimeStart: start,
                  datetimeEnd: end,
                })
              }}
            />
          </Field>
          <Field>
            <label>{translations.vehicle}</label>
            <VehicleSelect
              mode="single"
              value={form.formValues.vehicle}
              currentlyOwned
              clearable
              onChange={vehicle =>
                form.updateForm({ vehicle: vehicle?.id ?? '' })
              }
            />
          </Field>
          <Field>
            <label>{translations.driverBefore}</label>
            <UserCombobox
              mode="single"
              value={form.formValues.previousDriverUser}
              className="min-w-0"
              filterLeaderGroup
              onChange={user => form.updateForm({ previousDriverUser: user })}
            />
          </Field>
          <Field>
            <label>{translations.driverAfter}</label>
            <UserCombobox
              mode="single"
              value={form.formValues.nextDriverUser}
              className="min-w-0"
              filterLeaderGroup
              onChange={user => form.updateForm({ nextDriverUser: user })}
            />
          </Field>
          <Field>
            <label>{translations.sameDriverBeforeAndAfter}</label>
            <div className="flex h-[3.875rem] items-center px-4">
              <Checkbox
                value={form.formValues.sameDriverBeforeAndAfter}
                onChange={checked =>
                  form.updateForm({
                    sameDriverBeforeAndAfter: checked === true,
                  })
                }
              />
            </div>
          </Field>
          <Field>
            <label>{translations.status}</label>
            <div className="flex h-[3.875rem] items-center">
              <Toggles
                wide
                size="sm"
                className="min-w-0"
                value={form.formValues.explanationStatus}
                options={explanationStatusItems}
                onChange={explanationStatus =>
                  form.updateForm({ explanationStatus })
                }
              />
            </div>
          </Field>
        </section>
      </Card>

      <div className="flex flex-col gap-2">
        {activityGroups.map(group => {
          return (
            <Card
              key={group.id}
              className="border border-[#E1E1E1] bg-white px-5 py-4"
            >
              <div className="flex justify-between gap-4 max-md:flex-col">
                <H3>{group.vehicleLabel}</H3>
                <div className="flex items-center gap-3 max-md:flex-col max-md:items-start">
                  <div>
                    <strong>{translations.period}: </strong>{' '}
                    {formatPeriod(group.startDate, group.endDate)}
                  </div>
                  <DrivingWithoutCardMapDialog
                    group={group}
                    translations={{
                      map: translations.map,
                      period: translations.period,
                      loading: translations.loading,
                      noData: translations.noData,
                    }}
                  />
                </div>
              </div>
              <div className="my-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                <div className="min-w-0 rounded-md border border-[#E1E1E1] bg-[#F3F3F3] p-3">
                  <span className="mb-1 block text-xs text-[#767689]">
                    {translations.driving}
                  </span>
                  <strong>
                    {durationAsSecondsToString(group.durationMinutes * 60)}
                  </strong>
                </div>
                <div className="min-w-0 rounded-md border border-[#E1E1E1] bg-[#F3F3F3] p-3">
                  <span className="mb-1 block text-xs text-[#767689]">
                    {translations.events}
                  </span>
                  <strong>{group.entries.length}</strong>
                </div>
                <div className="min-w-0 rounded-md border border-[#E1E1E1] bg-[#F3F3F3] p-3">
                  <span className="mb-1 block text-xs text-[#767689]">
                    {translations.driverBefore}
                  </span>
                  <strong className="break-words">
                    {group.previousDriverName ?? 'N/A'}
                  </strong>
                </div>
                <div className="min-w-0 rounded-md border border-[#E1E1E1] bg-[#F3F3F3] p-3">
                  <span className="mb-1 block text-xs text-[#767689]">
                    {translations.driverAfter}
                  </span>
                  <strong className="break-words">
                    {group.nextDriverName ?? 'N/A'}
                  </strong>
                </div>
              </div>
              <DrivingWithoutCardGantt group={group} />
              <NearbyVehicleExpirations
                group={group}
                translations={{
                  vehicleExpirations: translations.vehicleExpirations,
                  date: translations.date,
                  description: translations.description,
                }}
              />
              <div className="mt-4 grid gap-2">
                {group.entries.map(entry => {
                  const startDate = parseISO(entry.start)
                  const endDate = parseISO(entry.end)

                  return (
                    <div
                      key={entry.id}
                      className="grid grid-cols-1 items-center gap-1 rounded-md border border-[#E1E1E1] px-3 py-2 md:grid-cols-[1fr_auto] md:gap-4"
                    >
                      <div>{formatPeriod(startDate, endDate)}</div>
                      <strong>
                        {entry.duration ??
                          durationAsSecondsToString(
                            differenceInMinutes(endDate, startDate) * 60
                          )}
                      </strong>
                    </div>
                  )
                })}
              </div>
              <DrivingWithoutCardExplanation
                group={group}
                translations={{
                  comment: translations.comment,
                  save: translations.save,
                }}
              />
            </Card>
          )
        })}
      </div>
    </div>
  )
}

const DrivingWithoutCardMapDialog: React.FC<{
  group: ActivityWithoutCardGroup
  translations: {
    map: string
    period: string
    loading: string
    noData: string
  }
}> = ({ group, translations }) => {
  const [open, setOpen] = useState(false)
  const vehicleVin = getVehicleVin(group)

  const { data, loading } = useQuery(DRIVER_LOCATIONS_FOR_VEHICLE_PERIOD, {
    skip: !open || !vehicleVin,
    variables: {
      vehicleVin: vehicleVin ?? '',
      datetimeStart: formatISO(
        subMinutes(group.startDate, DRIVER_LOCATION_PERIOD_PADDING_MINUTES)
      ),
      datetimeEnd: formatISO(
        addMinutes(group.endDate, DRIVER_LOCATION_PERIOD_PADDING_MINUTES)
      ),
    },
  })

  const vehicleLocations = useMemo(() => {
    const locations = data?.driverLocationsForVehiclePeriod ?? []

    return getSegmentedVehicleLocations({
      locations,
      vehicleVin,
      startDate: group.startDate,
      endDate: group.endDate,
    })
  }, [
    data?.driverLocationsForVehiclePeriod,
    group.endDate,
    group.startDate,
    vehicleVin,
  ])

  const pathColors = useMemo(() => {
    const colors = generateActivityMapColors(1)
    if (!vehicleVin) return {}

    return {
      [getVehicleLocationSegmentKey(
        vehicleVin,
        BEFORE_DRIVER_LOCATION_SEGMENT
      )]: PADDED_DRIVER_LOCATION_COLOR,
      [getVehicleLocationSegmentKey(vehicleVin, EVENT_DRIVER_LOCATION_SEGMENT)]:
        colors[0] ?? '#0088FF',
      [getVehicleLocationSegmentKey(vehicleVin, AFTER_DRIVER_LOCATION_SEGMENT)]:
        PADDED_DRIVER_LOCATION_COLOR,
    }
  }, [vehicleVin])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        size="sm"
        variant="outline"
        disabled={!vehicleVin}
        onClick={() => setOpen(true)}
      >
        <MapIcon />
        {translations.map}
      </Button>
      <DialogContent className="grid h-[min(46rem,calc(100vh-2rem))] w-[min(112rem,calc(100vw-2rem))] !max-w-[min(112rem,calc(100vw-2rem))] grid-rows-[auto_1fr] gap-4 p-5">
        <DialogHeader>
          <DialogTitle>{translations.map}</DialogTitle>
          <DialogDescription>
            {group.vehicleLabel} · {translations.period}:{' '}
            {formatPeriodText(group.startDate, group.endDate)}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 overflow-hidden rounded-md border border-[#E1E1E1]">
          {loading ? (
            <div className="flex h-full items-center justify-center text-sm text-[#767689]">
              {translations.loading}
            </div>
          ) : vehicleLocations.length ? (
            <ActivityMap
              className="h-full"
              mapContainerStyle={{ height: '100%', borderTopRightRadius: 0 }}
              pathColors={pathColors}
              vehicleLocations={vehicleLocations}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#767689]">
              {translations.noData}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

const DrivingWithoutCardExplanation: React.FC<{
  group: ActivityWithoutCardGroup
  translations: {
    comment: string
    save: string
  }
}> = ({ group, translations }) => {
  const initialValue =
    group.entries.find(entry => entry.explanation.trim())?.explanation ?? ''
  const [value, setValue] = useState(initialValue)
  const [savedValue, setSavedValue] = useState(initialValue)
  const [saving, setSaving] = useState(false)
  const [saveExplanation] = useMutation(
    UPDATE_DRIVING_WITHOUT_CARD_EVENT_EXPLANATION
  )

  const dirty = value.trim() !== savedValue.trim()

  const handleSave = async () => {
    setSaving(true)
    try {
      const results = await Promise.all(
        group.entries.map(entry =>
          saveExplanation({
            variables: {
              eventId: entry.id,
              explanation: value,
            },
          })
        )
      )
      const nextValue =
        results[0]?.data?.updateDrivingWithoutCardEventExplanation?.event
          ?.explanation ?? value.trim()
      setValue(nextValue)
      setSavedValue(nextValue)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-4 grid min-w-0 gap-2">
      <span className="text-xs text-[#767689]">{translations.comment}</span>
      <Textarea
        aria-label={translations.comment}
        className="min-h-20 resize-y text-sm"
        rows={3}
        value={value}
        onChange={setValue}
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          disabled={!dirty || saving}
          onClick={handleSave}
        >
          {translations.save}
        </Button>
      </div>
    </div>
  )
}

const DrivingWithoutCardGantt: React.FC<{
  group: ActivityWithoutCardGroup
}> = ({ group }) => {
  const groupDurationMinutes = Math.max(
    differenceInMinutes(group.endDate, group.startDate),
    1
  )

  return (
    <div className="mt-4">
      <div className="mb-1 flex justify-between gap-4 text-xs text-[#767689]">
        <span>{format(group.startDate, 'dd.MM.yyyy HH:mm')}</span>
        <span>{format(group.endDate, 'dd.MM.yyyy HH:mm')}</span>
      </div>
      <div className="relative h-9 overflow-hidden rounded-md border border-[#E1E1E1] bg-[repeating-linear-gradient(90deg,#F3F3F3,#F3F3F3_3.5rem,#e9ecef_3.5rem,#e9ecef_3.5625rem)]">
        {group.entries.map(entry => {
          const startDate = parseISO(entry.start)
          const endDate = parseISO(entry.end)
          const left =
            (differenceInMinutes(startDate, group.startDate) /
              groupDurationMinutes) *
            100
          const width = Math.max(
            (differenceInMinutes(endDate, startDate) / groupDurationMinutes) *
              100,
            0.5
          )
          const clampedLeft = Math.max(0, Math.min(left, 100))
          const availableWidth = Math.max(0, 100 - clampedLeft)
          const clampedWidth = Math.max(
            Math.min(0.5, availableWidth),
            Math.min(width, availableWidth)
          )

          return (
            <div
              key={entry.activity.id}
              className="absolute top-[0.45rem] h-5 min-w-1 rounded bg-[#fa1852]"
              title={`${formatPeriodText(startDate, endDate)} · ${
                entry.duration ??
                durationAsSecondsToString(
                  differenceInMinutes(endDate, startDate) * 60
                )
              }`}
              style={{
                left: `${clampedLeft}%`,
                width: `${clampedWidth}%`,
              }}
            />
          )
        })}
      </div>
    </div>
  )
}

const NearbyVehicleExpirations: React.FC<{
  group: ActivityWithoutCardGroup
  translations: {
    vehicleExpirations: string
    date: string
    description: string
  }
}> = ({ group, translations }) => {
  const expirations = getNearbyVehicleExpirations(group)

  if (!expirations.length) return null

  return (
    <div className="mt-4 rounded-md border border-[#E1E1E1] bg-[#F8F8F8] p-3">
      <div className="mb-2 text-sm font-semibold">
        {translations.vehicleExpirations}
      </div>
      <div className="grid gap-2">
        {expirations.map(expiration => {
          const expirationType = getVehicleExpirationTypeName({
            name: expiration.expirationType?.name ?? undefined,
          })

          return (
            <div
              key={expiration.id}
              className="grid grid-cols-1 gap-1 rounded border border-[#E1E1E1] bg-white px-3 py-2 text-sm md:grid-cols-[minmax(10rem,1fr)_minmax(10rem,1fr)_minmax(12rem,2fr)]"
            >
              <div className="min-w-0">
                <span className="mb-0.5 block text-xs text-[#767689]">
                  {translations.date}
                </span>
                <strong className="break-words">
                  {formatExpirationPeriod(expiration)}
                </strong>
              </div>
              <div className="min-w-0">
                <span className="mb-0.5 block text-xs text-[#767689]">
                  {translations.vehicleExpirations}
                </span>
                <strong className="break-words">{expirationType}</strong>
              </div>
              <div className="min-w-0">
                <span className="mb-0.5 block text-xs text-[#767689]">
                  {translations.description}
                </span>
                <span className="break-words">
                  {expiration.description ||
                    expiration.responsibleUser?.fullName ||
                    '-'}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function groupRelatedActivitiesWithoutCard(
  activities: DrivingWithoutCardEventNode[],
  unknownLabel: string
): ActivityWithoutCardGroup[] {
  const sortedActivities = [...activities].sort((a, b) => {
    const vehicleCompare = getVehicleKey(a).localeCompare(getVehicleKey(b))
    if (vehicleCompare !== 0) return vehicleCompare

    return parseISO(a.start).getTime() - parseISO(b.start).getTime()
  })

  const groups: ActivityWithoutCardGroup[] = []
  let currentEntries: DrivingWithoutCardEventNode[] = []

  const flushGroup = () => {
    if (!currentEntries.length) return

    groups.push(createActivityGroup(currentEntries, unknownLabel))
    currentEntries = []
  }

  for (const entry of sortedActivities) {
    const previousEntry = currentEntries[currentEntries.length - 1]

    if (!previousEntry || areRelatedActivities(previousEntry, entry)) {
      currentEntries.push(entry)
      continue
    }

    flushGroup()
    currentEntries.push(entry)
  }

  flushGroup()

  return groups
}

function areRelatedActivities(
  previousEntry: DrivingWithoutCardEventNode,
  entry: DrivingWithoutCardEventNode
) {
  if (getVehicleKey(previousEntry) !== getVehicleKey(entry)) return false

  const previousEnd = parseISO(previousEntry.end)
  const nextStart = parseISO(entry.start)
  const gapMinutes = differenceInMinutes(nextStart, previousEnd)

  return gapMinutes <= RELATED_EVENT_GAP_MINUTES
}

function createActivityGroup(
  entries: DrivingWithoutCardEventNode[],
  unknownLabel: string
): ActivityWithoutCardGroup {
  const firstEntry = entries[0]
  const lastEntry = entries[entries.length - 1]

  if (!firstEntry || !lastEntry) {
    throw new Error(
      'Cannot create a driving without card group without entries'
    )
  }

  const startDate = parseISO(firstEntry.start)
  const endDate = entries.reduce((latestEnd, entry) => {
    const entryEnd = parseISO(entry.end)
    return entryEnd > latestEnd ? entryEnd : latestEnd
  }, parseISO(firstEntry.end))
  const durationMinutes = entries.reduce((sum, entry) => {
    return (
      sum +
      Math.max(
        differenceInMinutes(parseISO(entry.end), parseISO(entry.start)),
        0
      )
    )
  }, 0)

  return {
    id: entries.map(entry => entry.id).join(':'),
    vehicleLabel: getVehicleLabel(firstEntry, unknownLabel),
    startDate,
    endDate,
    durationMinutes,
    previousDriverName: getNeighbourDriverLabel(
      firstEntry.previousDriverActivity
    ),
    nextDriverName: getNeighbourDriverLabel(lastEntry.nextDriverActivity),
    entries,
  }
}

function getNeighbourDriverLabel(
  activity?: DrivingWithoutCardNeighbourActivityNode | null
) {
  return activity?.user?.fullName || activity?.driverCardNumber || null
}

function getVehicleKey(entry: DrivingWithoutCardEventNode) {
  return (
    entry.vehicle?.id ??
    entry.vehicle?.vin ??
    entry.vehicleVin ??
    entry.activity.vehicleVin ??
    ''
  )
}

function getVehicleVin(group: ActivityWithoutCardGroup) {
  return (
    group.entries[0]?.vehicle?.vin ??
    group.entries[0]?.vehicleVin ??
    group.entries[0]?.activity.vehicleVin ??
    null
  )
}

function getSegmentedVehicleLocations({
  locations,
  vehicleVin,
  startDate,
  endDate,
}: {
  locations: readonly DriverLocationForMap[]
  vehicleVin?: string | null
  startDate: Date
  endDate: Date
}) {
  if (!vehicleVin) return []

  const beforeLocations: DriverLocationForMap[] = []
  const eventLocations: DriverLocationForMap[] = []
  const afterLocations: DriverLocationForMap[] = []

  for (const location of locations) {
    const locationDate = parseISO(location.datetimeStart)

    if (locationDate < startDate) {
      beforeLocations.push(location)
    } else if (locationDate > endDate) {
      afterLocations.push(location)
    } else {
      eventLocations.push(location)
    }
  }

  const firstEventLocation = eventLocations[0]
  const lastEventLocation = eventLocations[eventLocations.length - 1]

  return [
    {
      vehicleVin: getVehicleLocationSegmentKey(
        vehicleVin,
        BEFORE_DRIVER_LOCATION_SEGMENT
      ),
      coordinates: getLocationCoordinates(
        firstEventLocation
          ? [...beforeLocations, firstEventLocation]
          : beforeLocations
      ),
    },
    {
      vehicleVin: getVehicleLocationSegmentKey(
        vehicleVin,
        EVENT_DRIVER_LOCATION_SEGMENT
      ),
      coordinates: getLocationCoordinates(eventLocations),
    },
    {
      vehicleVin: getVehicleLocationSegmentKey(
        vehicleVin,
        AFTER_DRIVER_LOCATION_SEGMENT
      ),
      coordinates: getLocationCoordinates(
        lastEventLocation
          ? [lastEventLocation, ...afterLocations]
          : afterLocations
      ),
    },
  ].filter(location => location.coordinates.length > 0)
}

function getVehicleLocationSegmentKey(vehicleVin: string, segment: string) {
  return `${vehicleVin}:${segment}`
}

function getLocationCoordinates(locations: readonly DriverLocationForMap[]) {
  return locations.map(location => ({
    lat: location.latitude,
    lng: location.longitude,
  }))
}

function getVehicleLabel(
  entry: DrivingWithoutCardEventNode,
  unknownLabel: string
) {
  return (
    entry.vehicle?.registrationNumber ??
    entry.vehicleRegistrationNumber ??
    entry.vehicle?.vin ??
    entry.vehicleVin ??
    entry.activity.vehicleVin ??
    unknownLabel
  )
}

function getNearbyVehicleExpirations(group: ActivityWithoutCardGroup) {
  const expirationById = new Map<
    string,
    DrivingWithoutCardNearbyVehicleExpirationNode
  >()

  for (const entry of group.entries) {
    for (const expiration of entry.nearbyVehicleExpirations ?? []) {
      expirationById.set(expiration.id, expiration)
    }
  }

  return [...expirationById.values()].sort(
    (a, b) =>
      getExpirationSortDate(a).getTime() - getExpirationSortDate(b).getTime()
  )
}

function getExpirationSortDate(
  expiration: DrivingWithoutCardNearbyVehicleExpirationNode
) {
  return parseISO(
    expiration.expirationFrom ??
      expiration.expirationDate ??
      expiration.expirationTo
  )
}

function formatExpirationPeriod(
  expiration: DrivingWithoutCardNearbyVehicleExpirationNode
) {
  if (expiration.expirationFrom && expiration.expirationTo) {
    return formatPeriodText(
      parseISO(expiration.expirationFrom),
      parseISO(expiration.expirationTo)
    )
  }

  return format(parseISO(expiration.expirationDate), 'dd.MM.yyyy HH:mm')
}

function formatPeriod(startDate: Date, endDate: Date) {
  return <>{formatPeriodText(startDate, endDate)}</>
}

function formatPeriodText(startDate: Date, endDate: Date) {
  const isSameDayValue = isSameDay(startDate, endDate)

  return `${format(startDate, 'dd.MM.yyyy HH:mm')} - ${
    isSameDayValue
      ? format(endDate, 'HH:mm')
      : format(endDate, 'dd.MM.yyyy HH:mm')
  }`
}
